import React from 'react';
import clsx from 'clsx';
import Skeleton from 'core/components/Skeleton';

const METRIC_CARD_SKELETON_KEYS = [
  'status',
  'pain',
  'condition',
  'adherence',
] as const;

const METADATA_COLUMN_SKELETON_KEYS = [
  'last-check-in',
  'reports',
  'date-range',
  'reviewed',
] as const;

const TIMELINE_DOT_SKELETON_KEYS = [
  'dot-1',
  'dot-2',
  'dot-3',
  'dot-4',
  'dot-5',
  'dot-6',
] as const;

const SUDDEN_WORSENING_STAT_SKELETON_KEYS = [
  'total-events',
  'frequency-trend',
  'most-recent',
] as const;

type ChartCardSkeletonProps = {
  showToggle?: boolean;
};

type MetadataColumnSkeletonProps = {
  isFirst: boolean;
};

function MetricCardSkeleton() {
  return (
    <div
      className="grid min-h-[8.75rem] grid-rows-[auto_1fr_auto] rounded-md border border-gray-200 border-l-[3px] border-l-gray-400 bg-gray-400/5 px-3.5 py-3"
    >
      <Skeleton variant="rectangular" className="!h-3 !w-1/2 rounded" />
      <div className="flex items-center">
        <Skeleton variant="rectangular" className="!h-7 !w-3/4 rounded" />
      </div>
      <Skeleton variant="rectangular" className="!h-3 !w-2/3 rounded" />
    </div>
  );
}

function MetadataColumnSkeleton({ isFirst }: MetadataColumnSkeletonProps) {
  return (
    <div
      className={clsx(
        'rtm-metadata-item flex flex-1 min-w-0 items-center gap-2',
        { 'pl-4': !isFirst },
      )}
    >
      <Skeleton variant="circular" className="!h-9 !w-9 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Skeleton variant="rectangular" className="!h-3 !w-20 rounded" />
        <Skeleton variant="rectangular" className="!h-4 !w-28 rounded" />
      </div>
    </div>
  );
}

function MetadataBarSkeleton() {
  return (
    <div className="rtm-metadata-bar flex items-stretch divide-x divide-gray-200 rounded-lg border border-gray-200 p-4 shadow">
      {METADATA_COLUMN_SKELETON_KEYS.map((columnKey, columnIndex) => (
        <MetadataColumnSkeleton
          key={columnKey}
          isFirst={columnIndex === 0}
        />
      ))}
    </div>
  );
}

function BarriersSkeleton() {
  return (
    <div className="rtm-barriers-section flex w-fit min-w-[calc(25%-16px)] flex-col gap-1">
      <Skeleton variant="rectangular" className="!h-3 !w-48 rounded" />
      <Skeleton variant="rectangular" className="mt-0.5 !h-5 !w-36 rounded-full" />
    </div>
  );
}

function ChartCardSkeleton({ showToggle = false }: ChartCardSkeletonProps) {
  return (
    <div className="rounded-2xl border border-gray-200 p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <Skeleton variant="rectangular" className="!h-3 !w-40 rounded" />
          <Skeleton variant="rectangular" className="!h-5 !w-24 rounded-full" />
          <Skeleton variant="rectangular" className="!h-5 !w-20 rounded-full" />
        </div>
        {showToggle ? (
          <Skeleton variant="rectangular" className="!h-7 !w-36 shrink-0 rounded-md" />
        ) : null}
      </div>
      <Skeleton variant="rectangular" className="!h-44 !w-full rounded-xl" />
    </div>
  );
}

function SuddenWorseningStatCardSkeleton() {
  return (
    <div className="rounded-md border border-gray-200 bg-stone-50/70 px-3.5 py-3 opacity-90">
      <Skeleton variant="rectangular" className="!h-3 !w-24 rounded" />
      <Skeleton variant="rectangular" className="mt-2 !h-5 !w-16 rounded" />
    </div>
  );
}

function EventTimelineSkeleton() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-stone-50 p-4">
      <div className="relative">
        <div className="absolute left-0 right-0 top-1.5 h-px bg-gray-200" />
        <div className="relative flex justify-between gap-2">
          {TIMELINE_DOT_SKELETON_KEYS.map((dotKey) => (
            <div
              key={dotKey}
              className="flex min-w-0 flex-1 flex-col items-center gap-1.5"
            >
              <Skeleton variant="circular" className="!h-3 !w-3 shrink-0" />
              <Skeleton variant="rectangular" className="!h-3 !w-10 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SuddenWorseningSectionSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton variant="rectangular" className="!h-3 !w-36 rounded" />
      <div className="rtm-sudden-worsening-stats grid grid-cols-3 gap-3.5">
        {SUDDEN_WORSENING_STAT_SKELETON_KEYS.map((statKey) => (
          <SuddenWorseningStatCardSkeleton key={statKey} />
        ))}
      </div>
      <EventTimelineSkeleton />
    </div>
  );
}

export default function PatientTrendsDialogSkeleton() {
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-col gap-5">
        <MetadataBarSkeleton />

        <div className="flex flex-col">
          <Skeleton variant="rectangular" className="!h-5 !w-40 rounded" />
          <div className="rtm-metric-cards mt-1 grid grid-cols-4 gap-6">
            {METRIC_CARD_SKELETON_KEYS.map((metricKey) => (
              <MetricCardSkeleton key={metricKey} />
            ))}
          </div>
        </div>

        <BarriersSkeleton />
      </div>

      <div className="flex flex-col gap-1">
        <Skeleton variant="rectangular" className="!h-5 !w-20 rounded" />
        <div className="flex flex-col gap-3.5">
          <ChartCardSkeleton showToggle />
          <div className="rtm-lower-charts grid grid-cols-2 gap-3.5">
            <ChartCardSkeleton />
            <ChartCardSkeleton />
          </div>
          <SuddenWorseningSectionSkeleton />
        </div>
      </div>
    </div>
  );
}
